'use client';

import { useTranslations } from 'next-intl';
import React, { useCallback, useRef } from 'react';

import SpecialsSectionItem from '@/app/[locale]/[domain]/(default-layout)/_components/home-specials-section/SpecialsSectionItem';
import SectionTitle from '@/app/[locale]/[domain]/(default-layout)/_components/SectionTitle';

import ListLoadingSpinner from '@/components/animation/ListLoadingSpinner';

import { ShowingTagListResponse } from '@cineamo/api/types/ApiListResponse.types';
import { ApiResponse } from '@cineamo/api/types/ApiResponse.types';

import { getShowingTags } from '@/utils/api/showing-tag/showing-tag-api';

import { startOfProgramToday } from '@/helpers/program-day';
import { cn } from '@cineamo/ui/src/helpers/cn';
import { styleFaded } from '@cineamo/ui/src/helpers/style-helper';

import { usePagination } from '@/hooks/usePagination';
import useTrackVisibility from '@/hooks/useTrackVisibility';
import useScrollState from '@cineamo/ui/src/hooks/useScrollState';

import BackAndForthButtonOverlay from '@cineamo/ui/src/button/BackAndForthButtonOverlay';

import CenterLayout from '@cineamo/composables/src/CenterLayout';
import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';
import { formatDate_yyyyMMddTHHmmssZ } from '@/utils/date-helper';

import SpecialsSectionListItemSkeleton from '../skeletons/SpecialsSectionListItemSkeleton';

type SpecialsSectionContentProps = {
    showingTags: ApiResponse<unknown, ShowingTagListResponse> | null;
};

const SpecialsSectionContent: React.FC<SpecialsSectionContentProps> = (props) => {
    const domainConfiguration = useDomainConfiguration();
    const { track } = useAnalytics();
    const { cinema } = domainConfiguration;
    const cinemaId = cinema.id;
    const startDatetime = formatDate_yyyyMMddTHHmmssZ(startOfProgramToday(domainConfiguration.timeZone));

    const t = useTranslations();
    const specialsScrollContainerRef = useRef(null);

    const { styleFadedState } = useScrollState({
        scrollContainerRef: specialsScrollContainerRef,
        initialState: {
            startReached: true,
            endReached: false
        }
    });

    const {
        ref,
        data: showingTagListResponse,
        isFetching: isFetchingShowingList,
        hasNextPage: hasNextPageShowingList
    } = usePagination(
        [
            // `infinite` prefix added because of this https://tkdodo.eu/blog/effective-react-query-keys#caching-data
            'infinite-showing-tags',
            `cinemaIds-${[cinemaId]}`,
            `hasShowings-${true}`,
            `showingStartDatetime-${startDatetime}`
        ],
        async (pageParam) => {
            return await getShowingTags({
                cinemaIds: [cinemaId],
                hasShowings: true,
                showingStartDatetime: startDatetime,
                page: pageParam ?? 1
            });
        },
        props.showingTags
            ? {
                  initialData: {
                      pages: [props.showingTags],
                      pageParams: [props.showingTags._page]
                  }
              }
            : undefined
    );

    const showingTags = showingTagListResponse?.pages?.flatMap((page) => page?._embedded?.['showing-tags'] || []) || [];

    const fadeStyle = styleFaded(styleFadedState, 5);

    const sectionRef = useRef<HTMLElement>(null);

    const trackVisibility = useCallback(() => {
        track('Homepage Section Shown', {
            deviceWidth: window.innerWidth,
            deviceHeight: window.innerHeight,
            section: 'Specials'
        });
    }, [track]);

    useTrackVisibility(sectionRef, trackVisibility);

    return (
        <section
            ref={sectionRef}
            role="group"
            aria-label={t('specials.events')}>
            <CenterLayout className="space-y-12 lg:space-y-16">
                <SectionTitle title={t('specials.events')} />
                <div className="relative flex flex-row">
                    <div
                        className="relative w-full flex flex-row space-x-16 overflow-x-auto no-scrollbar"
                        style={fadeStyle}
                        ref={specialsScrollContainerRef}>
                        {showingTags.map((showingTag) => (
                            <SpecialsSectionItem
                                key={`SpecialsSectionItem#${showingTag.id}`}
                                showingTag={showingTag}
                            />
                        ))}

                        <div className="flex flex-row">
                            <div
                                className="loader-element"
                                ref={(node) => ref(node ?? undefined)}
                            />

                            {(hasNextPageShowingList !== false || isFetchingShowingList) &&
                                [...Array(3)].map((_, index) => (
                                    <SpecialsSectionListItemSkeleton key={`SpecialsSectionListItemSkeleton${index}`} />
                                ))}
                        </div>

                        {hasNextPageShowingList !== false && (
                            <ListLoadingSpinner
                                className={cn(
                                    'flex-none pr-24 w-min',
                                    { 'opacity-0': !isFetchingShowingList },
                                    { 'w-auto': showingTags.length > 0 }
                                )}
                            />
                        )}
                    </div>
                    {showingTags.length > 0 && (
                        <BackAndForthButtonOverlay
                            scrollContainerRef={specialsScrollContainerRef}
                            renderOutside
                        />
                    )}
                </div>
            </CenterLayout>
        </section>
    );
};

export default SpecialsSectionContent;
